import Core                   from '@/core/Core';
import Driver                 from '@/domains/Drivers/Driver';
import DriversDAO             from '@/domains/Drivers/DriversDAO';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

/**
 * Сервис для работы с водителями
 */
class DriversService {

  // ============================================================
  // Чтение
  // ============================================================

  /**
   * Загрузить одного водителя по id
   */
  static async get(id: number): Promise<Driver | null> {
    const response = await DriversDAO.get({ id });

    return Core.utils.hydrate(response.data, Driver.fromServer);
  }

  /**
   * Загрузить избранного водителя текущего пользователя.
   * null — избранного нет
   */
  static async getFavorite(): Promise<Driver | null> {
    const response = await DriversDAO.get({ isFavorite: true });

    return Core.utils.hydrate(response.data, Driver.fromServer);
  }

  /**
   * Загрузить список водителей с пагинацией для реестра через DataGridLoader
   */
  static async getListPaginated(
    params?: Parameters<typeof DriversDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Driver[]; pagination: RpcDataPagination | null }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await DriversDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Driver.fromServer),
      pagination: response.meta?.pagination ?? null,
    };
  }

  /**
   * Загрузить список водителей без пагинации
   */
  static async getList(params?: Parameters<typeof DriversDAO.getList>[0]): Promise<Driver[]> {
    return (await this.getListPaginated(params)).data;
  }

  // ============================================================
  // Запись
  // ============================================================

  /**
   * Создать или обновить водителя.
   * Возвращает id водителя, null — сохранить не удалось
   */
  static async upsert(driver: Driver): Promise<number | null> {
    const response = await DriversDAO.upsert(driver);

    if (response.ok) {
      if (driver.isExisting) {
        Core.showNotification.success('Водитель обновлён');
      }
      else {
        Core.showNotification.success('Водитель добавлен');
      }

      return response.data;
    }
    else {
      return null;
    }
  }

  /**
   * Переключить избранное текущего пользователя.
   * Уходит обычным upsert с перевёрнутым флагом isFavorite, сам водитель при пустом diff не меняется.
   * Строку реестра не трогаем — флаг меняется на копии, реестр перечитывается после сохранения
   */
  static async toggleFavorite(driver: Driver): Promise<boolean> {
    const updatedDriver = Core.utils.cloneDeep(driver);
    updatedDriver.isFavorite = driver.isFavorite === false;

    const response = await DriversDAO.upsert(updatedDriver);

    if (response.ok) {
      if (updatedDriver.isFavorite) {
        Core.showNotification.success('Водитель добавлен в избранное');
      }
      else {
        Core.showNotification.success('Водитель убран из избранного');
      }
    }

    return response.ok;
  }

  /**
   * Иконка звезды избранного водителя для реестра.
   * Под курсором звезда закрашена всегда, цвет задаёт состояние кнопки (у заблокированной — серый).
   * Водитель не выбран — пустая звезда (кнопка в этом случае заблокирована)
   */
  static getFavoriteIcon(driver: Driver | null, isHovered: boolean = false): string {
    if (isHovered === true || driver?.isFavorite === true) {
      return 'star';
    }
    else {
      return 'star_border';
    }
  }

  /**
   * Иконка одной из пяти звёзд оценки: закрашена, если оценка не ниже её номера;
   * половинка — для дробной средней оценки (3,5 → три полных и половинка). У целых оценок половинок не бывает
   */
  static getRateStarIcon(rate: number | null | undefined, star: number): string {
    if (Core.utils.isNullOrUndefined(rate) === true) {
      return 'star_border';
    }
    else if (rate >= star) {
      return 'star';
    }
    else if (rate >= star - 0.5) {
      return 'star_half';
    }
    else {
      return 'star_border';
    }
  }

  /**
   * Подсказка к звезде избранного водителя.
   * Водитель не выбран — подсказка, почему кнопка заблокирована
   */
  static getFavoriteTitle(driver: Driver | null): string {
    if (Core.utils.isNullOrUndefined(driver) === true) {
      return 'Выберите водителя, чтобы добавить его в избранное';
    }
    else if (driver.isFavorite === true) {
      return 'Убрать водителя из избранного';
    }
    else {
      return 'Добавить водителя в избранное';
    }
  }

  /**
   * Удалить водителя
   */
  static async delete(id: number): Promise<boolean> {
    const response = await DriversDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Водитель удалён');
    }

    return response.ok;
  }

  /**
   * Удалить водителя с подтверждением через диалог
   */
  static async deleteWithConfirmation(driver: Driver): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить этого водителя?',
      `Водитель: "${driver.name}"`,
    );

    if (confirmed === true) {
      const isDeleted = await this.delete(driver.id);

      if (isDeleted === true) {
        driver.isDeleted = true;
      }

      return isDeleted;
    }
    else {
      return false;
    }
  }

  // ============================================================
  // Диалоги
  // ============================================================

  /**
   * Открыть карточку водителя. null — новый водитель.
   * Промис резолвится по закрытию карточки
   */
  static showDriverCard(driver: Driver | null): Promise<boolean> {
    return Core.dialogs.DriverCard.showAsPromise({ driver });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Пункты контекстного меню водителя для грида реестра и карточки
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (driver: Driver) => { return this.showDriverCard(driver); },
        null,
        false,
        true,
      ),
      new ContextMenuItem(
        'История изменений',
        'history',
        (driver: Driver) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'driver', entityId: driver.id, showAddComment: false }); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (driver: Driver) => { return this.deleteWithConfirmation(driver); },
      ),
    ];
  }
}

export default DriversService;